import {DestroyRef, inject, Injectable, NgZone} from '@angular/core';
import {TelemetrySegmentTypeEnum} from '@truelearn-web/data-access/telemetry';
import {catchError, EMPTY} from 'rxjs';

import {SignalTypeEnum} from 'libs/data-access/telemetry/src/lib/enums/signal-type.enum';
import {UserSignal, UserSignalRequest} from 'libs/data-access/telemetry/src/lib/models/user-signal.model';
import {TelemetryDataService} from 'libs/data-access/telemetry/src/lib/services/telemetry-data.service';
import {getOrSetWindowId} from 'libs/shared/http/src/lib/interceptors/window-id-header-interceptor';

// Send as soon as this many signals are waiting.
const batchSizeThreshold = 10;
// Hard ceiling on how long a signal waits before it is sent.
const batchTimeoutMilliseconds = 30000;

export interface TelemetrySignalInput {
    signalType: SignalTypeEnum;
    payload: Record<string, unknown>;
    contentTargetTypeId?: number | null;
    targetId?: number | null;
    // Epoch milliseconds at which the thing being signalled happened on the caller's side. Falls
    // back to the time capture() runs when the caller does not supply it.
    occurredAt?: number;
}

@Injectable({providedIn: 'root'})
export class TelemetryService {
    private readonly telemetryDataService = inject(TelemetryDataService);
    private readonly zone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    private queue: UserSignal[] = [];
    private readonly segments = new Map<TelemetrySegmentTypeEnum, string>();
    private memberSubscriptionId: number | null = null;
    private suppressed = false;
    private flushTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        const onPageHide = (): void => this.flushBeacon();
        const onVisibilityChange = (): void => {
            if (document.visibilityState === 'hidden') {
                this.flushBeacon();
            }
        };

        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('visibilitychange', onVisibilityChange);

        this.destroyRef.onDestroy(() => {
            window.removeEventListener('pagehide', onPageHide);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            this.clearTimer();
        });
    }

    // Stamps the signal with its own id and the time it occurred, encodes the payload as JSON, then
    // queues it for the next batch.
    capture(signal: TelemetrySignalInput): void {
        try {
            if (this.suppressed) {
                return;
            }

            this.enqueue({
                SignalId: window.crypto.randomUUID(),
                SignalType: signal.signalType,
                ContentTargetTypeId: signal.contentTargetTypeId ?? null,
                TargetId: signal.targetId ?? null,
                Payload: JSON.stringify({...signal.payload, ...this.segmentPayload()}),
                OccurredAt: new Date(signal.occurredAt ?? Date.now()).toISOString()
            });

            if (this.queue.length >= batchSizeThreshold) {
                this.flush();
            } else {
                this.armTimer();
            }
        } catch {
            // Capturing a signal never interrupts the caller
        }
    }

    // Replaces any active segment of the same type. Signals captured until it stops carry the
    // returned id in their payload under `<type>Id`.
    startSegment(type: TelemetrySegmentTypeEnum): string {
        const segmentId = window.crypto.randomUUID();
        this.segments.set(type, segmentId);

        return segmentId;
    }

    // Ignored when the segment has already been replaced, so a late stop never ends a newer one.
    stopSegment(type: TelemetrySegmentTypeEnum, segmentId: string): void {
        if (this.segments.get(type) === segmentId) {
            this.segments.delete(type);
        }
    }

    // Sends whatever is queued against the previous subscription before adopting the new one.
    setSubscription(memberSubscriptionId: number | null): void {
        this.flush();
        this.memberSubscriptionId = memberSubscriptionId;
    }

    // While suppressed nothing is captured or sent, and anything already queued is discarded.
    setSuppressed(suppressed: boolean): void {
        this.suppressed = suppressed;

        if (suppressed) {
            this.dequeue();
        }
    }

    flush(): void {
        if (this.suppressed) {
            return;
        }

        const signals = this.dequeue();

        if (signals.length === 0) {
            return;
        }

        this.telemetryDataService
            .postSignals(this.buildRequest(signals))
            .pipe(catchError(() => EMPTY))
            .subscribe();
    }

    flushBeacon(): void {
        if (this.suppressed) {
            return;
        }

        const signals = this.dequeue();

        if (signals.length === 0) {
            return;
        }

        this.telemetryDataService.postSignalsBeacon(this.buildRequest(signals));
    }

    private segmentPayload(): Record<string, string> {
        const payload: Record<string, string> = {};
        this.segments.forEach((segmentId, type) => (payload[`${type}Id`] = segmentId));

        return payload;
    }

    private enqueue(signal: UserSignal): void {
        this.queue.push(signal);
    }

    private dequeue(): UserSignal[] {
        this.clearTimer();

        const signals = this.queue;
        this.queue = [];

        return signals;
    }

    private buildRequest(signals: UserSignal[]): UserSignalRequest {
        return {
            Source: {
                SessionId: getOrSetWindowId(),
                MemberSubscriptionId: this.memberSubscriptionId
            },
            Signals: signals
        };
    }

    // Runs only while no timeout is pending, so the wait is measured from the first queued signal.
    private armTimer(): void {
        if (this.flushTimer !== null) {
            return;
        }

        this.zone.runOutsideAngular(() => {
            this.flushTimer = setTimeout(() => {
                this.flushTimer = null;

                try {
                    this.flush();
                } catch {
                    // Sending a batch never surfaces a failure to the application
                }
            }, batchTimeoutMilliseconds);
        });
    }

    private clearTimer(): void {
        if (this.flushTimer === null) {
            return;
        }

        clearTimeout(this.flushTimer);
        this.flushTimer = null;
    }
}
